import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
    Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Typography
} from '@material-ui/core'
import MuiAlert from '@material-ui/lab/Alert'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import ContactSupportIcon from '@material-ui/icons/ContactSupport'
import { IAMAction } from '../../actions'

/**
 * MigrationDialog — the full self-migration UX.
 *
 * 1. Explains what migration does (account created in the new app, temporary
 *    password emailed, TOTP reset).
 * 2. On confirm, triggers IAMAction.migrate.
 * 3. On SUCCESS shows a success message + "Open the new ESP app" button
 *    (uses the response newAppUrl, falling back to the OLD NEW_APP_URL config)
 *    and a note to check email for the temporary password.
 * 4. On ERROR shows a "Contact support" mailto: to the configured supportEmail.
 */
const MigrationDialog = ({ open, onClose }) => {
    const dispatch = useDispatch()
    const app = useSelector((state) => state.app)
    const migrationStatus = app.migrationStatus

    // newAppUrl from the migration response takes precedence over the config fallback.
    const [resultUrl, setResultUrl] = React.useState('')

    const configNewAppUrl = app.newAppUrl || ''
    const supportEmail = app.supportEmail || ''
    const openUrl = resultUrl || configNewAppUrl

    const supportMailto = `mailto:${supportEmail}?subject=${encodeURIComponent('ESP migration to the new app failed')}&body=${encodeURIComponent('Hello Support,\n\nI ran into an error while migrating my ESP account to the new app. Please assist.\n\nThank you.')}`

    const handleClose = () => {
        IAMAction.resetMigrationStatus(dispatch)
        setResultUrl('')
        onClose && onClose()
    }

    const handleMigrate = () => {
        IAMAction.migrate(dispatch).then((result) => {
            if (result && result.status === 'SUCCESS') {
                setResultUrl(result.newAppUrl || '')
            }
        })
    }

    return (
        <Dialog open={open} onClose={migrationStatus === 'WORKING' ? undefined : handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Migrate to the new Ericsson Support Portal</DialogTitle>
            <DialogContent dividers>
                {migrationStatus === 'SUCCESS' ? (
                    <React.Fragment>
                        <MuiAlert severity="success" variant="outlined" style={{ marginBottom: 16 }}>
                            Your account has been migrated to the new ESP app.
                        </MuiAlert>
                        <DialogContentText>
                            A <strong>temporary password</strong> has been emailed to you. Please check your
                            inbox (and spam folder). You will be asked to set a new password on your first
                            login to the new app. Two-Factor Authentication has been reset and can be
                            re-enabled after you sign in.
                        </DialogContentText>
                    </React.Fragment>
                ) : migrationStatus === 'ERROR' ? (
                    <React.Fragment>
                        <MuiAlert severity="error" variant="outlined" style={{ marginBottom: 16 }}>
                            We could not complete your migration.
                        </MuiAlert>
                        <DialogContentText>
                            Something went wrong while migrating your account. Please try again in a few
                            minutes. If the problem persists, contact support and we will help you move to
                            the new app.
                        </DialogContentText>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <DialogContentText>
                            The Ericsson Support Portal is moving to a new application. Migrating will:
                        </DialogContentText>
                        <ul>
                            <li><Typography variant="body1">Create your account in the new ESP app.</Typography></li>
                            <li><Typography variant="body1">Email you a <strong>temporary password</strong> — you will change it on first login.</Typography></li>
                            <li><Typography variant="body1">Reset your Two-Factor Authentication (you can re-enable it after signing in).</Typography></li>
                        </ul>
                        <DialogContentText>
                            Your username and profile details are carried over. This does not delete anything
                            in the old portal.
                        </DialogContentText>
                    </React.Fragment>
                )}
            </DialogContent>
            <DialogActions>
                {migrationStatus === 'SUCCESS' ? (
                    <React.Fragment>
                        <Button onClick={handleClose} color="default">Close</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<OpenInNewIcon />}
                            href={openUrl || undefined}
                            disabled={!openUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Open the new ESP app
                        </Button>
                    </React.Fragment>
                ) : migrationStatus === 'ERROR' ? (
                    <React.Fragment>
                        <Button onClick={handleClose} color="default">Close</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<ContactSupportIcon />}
                            href={supportEmail ? supportMailto : undefined}
                            disabled={!supportEmail}
                        >
                            Contact support
                        </Button>
                        <Button onClick={handleMigrate} color="secondary" variant="outlined">Retry</Button>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <Button onClick={handleClose} color="default" disabled={migrationStatus === 'WORKING'}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleMigrate}
                            disabled={migrationStatus === 'WORKING'}
                            startIcon={migrationStatus === 'WORKING' ? <CircularProgress size={18} /> : null}
                        >
                            {migrationStatus === 'WORKING' ? 'Migrating…' : 'Migrate my account'}
                        </Button>
                    </React.Fragment>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default MigrationDialog
